import { decodeJwt } from './jwt.js';
import { isBcSsoMessage } from './messages.js';
const STORAGE_KEY_DEFAULT = 'bc_session_v1';
const STORAGE_KEY_FALLBACK = 'bc_sso';
const inIframe = () => {
    try {
        return typeof window !== 'undefined' && window.parent !== window;
    }
    catch {
        return false;
    }
};
const exactOrigin = (candidate) => {
    if (!candidate || candidate === 'null' || candidate.includes('*'))
        return null;
    try {
        const url = new URL(candidate);
        if (url.origin === 'null' || `${url.origin}/` !== `${candidate.replace(/\/+$/, '')}/`)
            return null;
        return url.origin;
    }
    catch {
        return null;
    }
};
const readUrlToken = () => {
    if (typeof window === 'undefined')
        return { token: null, hasTokenMaterial: false };
    if (window.location.hash) {
        const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
        const params = new URLSearchParams(hash);
        const fromHash = params.get('bc_token') || (hash.includes('=') ? null : hash);
        if (params.has('bc_token') || (fromHash && fromHash.split('.').length === 3)) {
            return { token: fromHash, hasTokenMaterial: true };
        }
    }
    if (window.location.search) {
        const searchParams = new URLSearchParams(window.location.search);
        const fromSearch = searchParams.get('bc_token') || searchParams.get('token');
        if (searchParams.has('bc_token') || searchParams.has('token')) {
            return { token: fromSearch, hasTokenMaterial: true };
        }
    }
    return { token: null, hasTokenMaterial: false };
};
const clearHash = () => {
    if (typeof window === 'undefined')
        return;
    const url = new URL(window.location.href);
    url.searchParams.delete('bc_token');
    url.searchParams.delete('token');
    if (url.hash) {
        const hash = url.hash.slice(1);
        const hashParams = new URLSearchParams(hash);
        if (hashParams.has('bc_token')) {
            hashParams.delete('bc_token');
            const remaining = hashParams.toString();
            url.hash = remaining ? `#${remaining}` : '';
        }
        else if (!hash.includes('=') && hash.split('.').length === 3) {
            url.hash = '';
        }
    }
    try {
        window.history.replaceState(null, '', url.toString());
    }
    catch {
        // Ignore in restricted environments
    }
};
const readSession = (key) => {
    try {
        if (typeof sessionStorage === 'undefined')
            return null;
        let raw = sessionStorage.getItem(key);
        if (!raw && key === STORAGE_KEY_DEFAULT) {
            raw = sessionStorage.getItem(STORAGE_KEY_FALLBACK);
        }
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.token === 'string') {
            return parsed;
        }
        return null;
    }
    catch {
        return null;
    }
};
const writeSession = (key, state) => {
    try {
        if (typeof sessionStorage === 'undefined')
            return;
        sessionStorage.setItem(key, JSON.stringify(state));
    }
    catch {
        // In restricted sandbox without allow-same-origin, sessionStorage might throw.
        // In-memory state remains active.
    }
};
const clearSession = (key) => {
    try {
        if (typeof sessionStorage === 'undefined')
            return;
        sessionStorage.removeItem(key);
        sessionStorage.removeItem(STORAGE_KEY_FALLBACK);
    }
    catch {
        // Ignore storage errors
    }
};
/**
 * PostMessage & URL token delivery client.
 * Conforms to REMOTE_MODULE_RECEIVER.md §3 and §4.
 */
export class BcSsoClient {
    opts;
    allowedParentOrigins;
    parentOrigin;
    state = null;
    refreshTimer = null;
    expiryTimer = null;
    broadcastChannel = null;
    started = false;
    constructor(opts) {
        this.opts = {
            storageKey: STORAGE_KEY_DEFAULT,
            refreshLeadMs: 30_000,
            expectedAudience: typeof opts.expectedAudience === 'string' && opts.expectedAudience.length > 0
                ? opts.expectedAudience
                : `module:${opts.moduleId.replace(/^module:/i, '')}`,
            onToken: undefined,
            onError: undefined,
            onIdentityChange: undefined,
            ...opts,
        };
        const configuredOrigins = [opts.platformBase, ...opts.allowedParentOrigins]
            .map(exactOrigin)
            .filter((origin) => origin !== null);
        this.allowedParentOrigins = new Set(configuredOrigins);
        this.parentOrigin = exactOrigin(opts.platformBase) ?? configuredOrigins[0] ?? null;
    }
    /** Returns the verified token, or null if no token is currently available. */
    current() {
        if (this.state && Date.now() >= this.state.claims.exp * 1000) {
            this.clearAuthState();
            this.opts.onError?.('session_expired');
        }
        return this.state;
    }
    /** Clean up listeners and timers without wiping valid sessionStorage. */
    destroy() {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        if (this.expiryTimer) {
            clearTimeout(this.expiryTimer);
            this.expiryTimer = null;
        }
        if (this.broadcastChannel) {
            try {
                this.broadcastChannel.close();
            }
            catch {
                // Ignore
            }
            this.broadcastChannel = null;
        }
        if (typeof window !== 'undefined') {
            window.removeEventListener('message', this.onMessage);
            window.removeEventListener('focus', this.handleWindowFocus);
            window.removeEventListener('storage', this.handleStorageEvent);
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
        this.started = false;
    }
    /** Explicitly log out, wipe cached tokens and notify listeners. */
    logout() {
        this.clearAuthState();
        this.destroy();
        this.opts.onError?.('session_logged_out');
    }
    /**
     * Top-level file download trigger that safely bridges sandboxed iframes.
     * Conforms to REMOTE_MODULE_RECEIVER.md §4.
     */
    triggerDownload(url, filename) {
        if (typeof window !== 'undefined' && inIframe() && window.parent && this.parentOrigin) {
            window.parent.postMessage({
                type: 'bc_download',
                moduleId: this.opts.moduleId,
                url,
                filename: filename || 'download',
            }, this.parentOrigin);
        }
        else if (typeof document !== 'undefined') {
            const a = document.createElement('a');
            a.href = url;
            if (filename)
                a.download = filename;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    }
    /** Begin listening to URL hash/query and postMessage. Idempotent. */
    start() {
        if (this.started || typeof window === 'undefined')
            return;
        this.started = true;
        // 1. sessionStorage first (warm reload).
        const cached = readSession(this.opts.storageKey);
        if (cached) {
            clearSession(this.opts.storageKey);
            this.acceptToken(cached.token);
        }
        // 2. URL token from hash or query parameters (first load).
        const fromUrl = readUrlToken();
        if (fromUrl.hasTokenMaterial) {
            clearHash();
            if (fromUrl.token)
                this.acceptToken(fromUrl.token);
        }
        // 3. postMessage (preferred channel).
        window.addEventListener('message', this.onMessage);
        // 4. Request session from parent wrapper (handles both bc_request_session and bc-sso:ready).
        this.requestParentSession();
        // 5. Setup Window Focus & Visibility Listeners (detects workspace switch in parent platform or across tabs)
        window.addEventListener('focus', this.handleWindowFocus);
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
        // 6. Cross-tab storage & BroadcastChannel synchronization
        window.addEventListener('storage', this.handleStorageEvent);
        if ('BroadcastChannel' in window) {
            try {
                this.broadcastChannel = new BroadcastChannel('bc_workspace_sync');
                this.broadcastChannel.onmessage = (event) => {
                    const data = event.data;
                    if (data && (data.type === 'tenant_changed' || data.type === 'bc_workspace_changed')) {
                        const newTenantId = data.tenantId;
                        if (newTenantId && this.state && String(newTenantId) !== String(this.state.claims.tenantId)) {
                            this.requestParentSession();
                            if (!inIframe()) {
                                window.location.reload();
                            }
                        }
                    }
                };
            }
            catch {
                // Ignore BroadcastChannel errors in restrictive environments
            }
        }
    }
    handleVisibilityChange = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
            this.handleWindowFocus();
        }
    };
    handleStorageEvent = (event) => {
        if (event.key === 'bc_active_tenant' || event.key === 'bc_workspace_id' || event.key === 'bc_tenant_id') {
            if (event.newValue && this.state && event.newValue !== this.state.claims.tenantId) {
                this.requestParentSession();
                if (!inIframe()) {
                    window.location.reload();
                }
            }
        }
    };
    handleWindowFocus = async () => {
        if (typeof window === 'undefined')
            return;
        // In iframe: ask parent wrapper for current session token
        if (inIframe()) {
            this.requestParentSession();
            return;
        }
        // For standalone tabs: check if the parent platform's active workspace changed
        if (!this.state)
            return;
        try {
            const res = await fetch('/api/auth/profile', { credentials: 'include', cache: 'no-store' });
            if (!res.ok)
                return;
            const data = (await res.json().catch(() => ({})));
            const activePlatformTenantId = data?.profile?.tenantId;
            const currentModuleTenantId = this.state.claims.tenantId;
            if (activePlatformTenantId && currentModuleTenantId && String(activePlatformTenantId) !== String(currentModuleTenantId)) {
                window.location.reload();
            }
        }
        catch {
            // Ignore network/cors errors on background profile poll
        }
    };
    requestParentSession() {
        if (typeof window === 'undefined')
            return;
        if (inIframe() && window.parent && this.parentOrigin) {
            try {
                window.parent.postMessage({ type: 'bc_request_session', moduleId: this.opts.moduleId }, this.parentOrigin);
                window.parent.postMessage({ type: 'bc-sso:ready', origin: window.location.origin, moduleId: this.opts.moduleId }, this.parentOrigin);
            }
            catch {
                // Ignore iframe postMessage errors
            }
        }
    }
    onMessage = (event) => {
        if (!isBcSsoMessage(event.data))
            return;
        if (event.source !== window.parent)
            return;
        if (event.origin === 'null' || !this.allowedParentOrigins.has(event.origin))
            return;
        const msg = event.data;
        // Handle logout broadcast from parent platform (REMOTE_MODULE_RECEIVER.md §3.2)
        if (msg.type === 'bc_logout' || msg.type === 'bc-sso:logout' || msg.type === 'bc_session_terminated') {
            this.clearAuthState();
            this.opts.onError?.('session_terminated');
            return;
        }
        const token = msg.token ||
            msg.bc_token ||
            msg.data?.token;
        if (typeof token === 'string') {
            this.acceptToken(token);
        }
        else if (msg.type === 'bc-sso:reload') {
            window.location.reload();
        }
    };
    acceptToken(token) {
        let claims;
        try {
            claims = decodeJwt(token);
        }
        catch {
            this.opts.onError?.('malformed_jwt');
            return;
        }
        if (typeof claims.exp !== 'number' || !Number.isFinite(claims.exp) || Date.now() >= claims.exp * 1000) {
            this.opts.onError?.('session_expired');
            return;
        }
        if (claims.iss !== 'bizcentral-gateway') {
            this.opts.onError?.('iss_mismatch');
            return;
        }
        if (claims.aud !== this.opts.expectedAudience) {
            this.opts.onError?.('aud_mismatch');
            return;
        }
        if (typeof claims.sub !== 'string' ||
            claims.sub.trim().length === 0 ||
            typeof claims.tenantId !== 'string' ||
            claims.tenantId.trim().length === 0) {
            this.opts.onError?.('missing_claims');
            return;
        }
        if (claims.userId !== undefined && (typeof claims.userId !== 'string' || claims.userId !== claims.sub)) {
            this.opts.onError?.('identity_mismatch');
            return;
        }
        claims.userId = claims.sub;
        this.accept({ token, claims, raw: token });
    }
    accept(state) {
        const previousIdentity = this.state ? this.identityOf(this.state) : null;
        const currentIdentity = this.identityOf(state);
        this.state = state;
        writeSession(this.opts.storageKey, state);
        if (previousIdentity &&
            (previousIdentity.tenantId !== currentIdentity.tenantId || previousIdentity.userId !== currentIdentity.userId)) {
            this.opts.onIdentityChange?.({ previous: previousIdentity, current: currentIdentity });
        }
        this.opts.onToken?.(state);
        this.scheduleRefresh();
    }
    identityOf(state) {
        return { tenantId: state.claims.tenantId, userId: state.claims.sub };
    }
    clearAuthState() {
        this.state = null;
        clearSession(this.opts.storageKey);
        if (this.refreshTimer)
            clearTimeout(this.refreshTimer);
        if (this.expiryTimer)
            clearTimeout(this.expiryTimer);
        this.refreshTimer = null;
        this.expiryTimer = null;
    }
    scheduleRefresh() {
        if (!this.state || !this.state.claims.exp)
            return;
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        if (this.expiryTimer) {
            clearTimeout(this.expiryTimer);
            this.expiryTimer = null;
        }
        const expMs = this.state.claims.exp * 1000;
        const now = Date.now();
        const remainingMs = expMs - now;
        // Refresh lead time before expiry (e.g. 30s before)
        const leadMs = Math.min(this.opts.refreshLeadMs, Math.max(2_000, remainingMs / 2));
        const wait = Math.max(0, remainingMs - leadMs);
        this.refreshTimer = setTimeout(() => {
            this.requestParentSession();
        }, wait);
        this.expiryTimer = setTimeout(() => {
            if (this.state === null || this.state.claims.exp !== expMs / 1000)
                return;
            this.clearAuthState();
            this.opts.onError?.('session_expired');
        }, Math.max(0, remainingMs));
    }
}
